import { Form, Formik } from "formik";
import React, { useState, useEffect } from "react";
import AddButton from "../../buttons/button";
import ImageField from "../../forms/images-field";
import CheckBox from "../../forms/check-box-field";
import { useDispatch } from "react-redux";
import { StoreLOICreateAction } from "../../../redux/actions/store-action";
import { getLoiHtmlDataApi, getLoiPdfApi } from "../../../redux/apis/store-api";
import successToast from "../../helper-components/success-toast";
import errorToast from "../../helper-components/error-toast";
import LoadingSpinner from "../../helper-components/loading-spinner";
import ApproveDeclineActions from "../approve-decline-actions";
import {
  buildDocumentParty,
  fetchStorePartners,
  firstFilled,
  toLoiPartner,
} from "../document-payload";

const StoreLOITab = ({
  id,
  setStoreData,
  storeData,
  initialValue,
  permissionFn = () => {},
  disabled = false,
  // Reports a finished step to the tab container, which unlocks the next tab.
  onProgress = () => {},
}) => {
  const dispatch = useDispatch();
  const [loiPdfUrl, setLoiPdfUrl] = useState(initialValue?.agreement_document ?? initialValue?.loi_pdf_url ?? "");
  const [loiPdfSignature, setLoiPdfSignature] = useState(initialValue?.digital_sign ?? "");
  const [isPdfLoading, setIsPdfLoading] = useState(false);

  const fetchLoiPdf = (signature) => {
    if (!id) return Promise.resolve("");
    const resolvedSignature = signature ?? initialValue?.digital_sign ?? "";
    setIsPdfLoading(true);
    const buildLoiPayload = (partners) => ({
      ...buildDocumentParty(storeData, initialValue),
      store_type: firstFilled(storeData?.address?.store_type, initialValue?.store_type),
      designation: firstFilled(storeData?.firm?.designation, initialValue?.designation),
      signature: resolvedSignature,
      witness: firstFilled(
        initialValue?.loi_witness,
        storeData?.agreement?.agreement_witness,
        initialValue?.agreement_witness
      ),
      partner_details: partners.map(toLoiPartner),
    });

    return Promise.all([
      fetchStorePartners(id),
      getLoiHtmlDataApi({ store_id: id }),
    ])
      .then(([partners]) => getLoiPdfApi(buildLoiPayload(partners)))
      .then((res) => {
        const url =
          res.data?.data?.pdf_url ??
          res.data?.data?.url ??
          (typeof res.data?.data === "string" ? res.data.data : "") ??
          "";
        if (url) {
          setLoiPdfUrl(url);
          setLoiPdfSignature(resolvedSignature);
          return url;
        }
        errorToast("Failed to load LOI document");
        return "";
      })
      .catch(() => {
        errorToast("Failed to load LOI document");
        return "";
      })
      .finally(() => setIsPdfLoading(false));
  };

  useEffect(() => {
    if (id && !loiPdfUrl) {
      fetchLoiPdf();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const initialValues = {
    digital_sign: initialValue?.digital_sign ?? "",
    firm_agreement_with_davaindia_conditions:
      initialValue?.firm_agreement_with_davaindia_conditions ?? false,
    // LOI approval state, as the /store/approve and /store/reject endpoints name it.
    is_loi_approved: initialValue?.is_loi_approved ?? false,
    is_loi_rejected: initialValue?.is_loi_rejected ?? false,
  };

  const onSubmit = async (values) => {
    if (!id) {
      errorToast("Please save Store Details first");
      return;
    }
    if (!values.firm_agreement_with_davaindia_conditions) {
      errorToast("Please accept the terms and conditions");
      return;
    }

    // Regenerate only if there's no document yet or the signature has
    // changed since the currently-loaded PDF was generated, so the saved
    // agreement_document always reflects the signature being submitted.
    let finalPdfUrl = loiPdfUrl;
    if (!finalPdfUrl || values.digital_sign !== loiPdfSignature) {
      finalPdfUrl = await fetchLoiPdf(values.digital_sign);
    }

    if (!finalPdfUrl) {
      errorToast("Terms & conditions document not loaded");
      return;
    }

    const payload = {
      store_id: id,
      agreement_document: finalPdfUrl,
      digital_sign: values.digital_sign,
      firm_agreement_with_davaindia_conditions:
        values.firm_agreement_with_davaindia_conditions,
    };
    dispatch(StoreLOICreateAction(payload, onSuccess, onError));
  };

  const onSuccess = (data) => {
    setStoreData({ ...storeData, loi: data?.data });
    successToast(data?.message ?? data?.data?.message);
  };

  const onError = (data) => {
    errorToast(data?.data?.message ?? data?.message);
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      {({ setFieldValue, values, initialValues: savedValues }) => (
        <Form style={{ height: "calc(100% - 5rem)" }} className="pt-5">
          <div className="flex flex-col justify-between h-full">
            <div>
              <div className="w-full 2xl:w-[40%] xl:w-[50%] lg:w-[60%] px-5 xl:px-10">
                <ImageField
                  isDisabled={disabled}
                  label="Digital Signature"
                  labelWidth="10.5rem"
                  name="digital_sign"
                />
              </div>
              {(isPdfLoading || loiPdfUrl) && (
                <div className="px-5 xl:px-10 mt-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-600">
                      LOI Document
                    </p>
                    {!disabled && (
                      <button
                        type="button"
                        onClick={() => fetchLoiPdf(values.digital_sign)}
                        disabled={isPdfLoading}
                        className="text-primary fw_500 text-sm underline disabled:opacity-50"
                      >
                        Regenerate
                      </button>
                    )}
                  </div>
                  {isPdfLoading ? (
                    <div
                      className="flex items-center justify-center bg-EBEBEB rounded-md"
                      style={{ width: "100%", height: "800px" }}
                    >
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div style={{ width: "100%", height: "800px" }}>
                      <embed
                        src={loiPdfUrl}
                        type="application/pdf"
                        width="100%"
                        height="100%"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="sticky" style={{ bottom: "0" }}>
              <div
                style={{ height: "5rem" }}
                className="px-5 xl:px-10 bg-D0D5DD flex items-center"
              >
                <CheckBox items={loiTerms} />
              </div>
              <div className="flex items-center justify-between px-10 mb-5x bg-white py-4 invoice-bottom-container">
                {permissionFn("store_document_process")?.approver ? (
                  <ApproveDeclineActions
                    storeId={id}
                    approveKey="is_loi_approved"
                    rejectKey="is_loi_rejected"
                    record={initialValue}
                    isApproved={!!values?.is_loi_approved}
                    isRejected={!!values?.is_loi_rejected}
                    disabled={disabled}
                    alwaysAllowDecline={true}
                    formValues={values}
                    savedValues={savedValues}
                    onDecided={async (data) => {
                      onProgress({
                        is_loi_approved: !!data?.is_loi_approved,
                        is_loi_rejected: !!data?.is_loi_rejected,
                      });
                      await setFieldValue(
                        "is_loi_approved",
                        !!data?.is_loi_approved
                      );
                      await setFieldValue(
                        "is_loi_rejected",
                        !!data?.is_loi_rejected
                      );
                    }}
                  />
                ) : (
                  <span />
                )}
                {!disabled && (
                  <AddButton prefixIcon={false} text="Save" type="submit" />
                )}
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default StoreLOITab;

const loiTerms = [
  {
    label:
      "I acknowledge that I have read, and do hereby accept the terms and conditions contained in this manual.",
    name: "firm_agreement_with_davaindia_conditions",
    isRequired: false,
  },
];
